<template>
  <div
    class="container"
    :class="{ 'root-container': isRoot }">
    <li
      class="collection-item-container"
      v-for="collection in sortedCollections"
      :key="collection.id">
      <div
        class="collection-item"
        :class="{
          'drag-over': highlightDragOverCollection === collection.id,
        }"
        :draggable="movingCollection === collection.id"
        @dragstart="dragstartHandler($event, { collectionId: collection.id, name: collection.name })"
        @dragover.prevent
        @dragenter="dragenterHandler($event, collection)"
        @dragexit="setHighlightDragOverCollection(null)"
        @drop="dropHandler($event, collection)">
        <div class="collection-item-actions">
          <b-tooltip
            label="Move"
            type="is-black"
            position="is-bottom"
            :delay="150"
            square>
            <div class="collection-item-move" @mousedown="toggleMoveCollection(collection.id)" @mouseup="toggleMoveCollection(collection.id)">
              <span class="icon collection-option collection-option-move">
                <i class="mdi mdi-drag-horizontal move-icon" />
              </span>
            </div>
          </b-tooltip>
          <b-tooltip
            label="Show/hide in sidebar"
            type="is-black"
            position="is-bottom"
            :delay="150"
            square>
            <div class="collection-item-hide" @click="toggleHideCollection(collection)">
              <span
                v-if="hiddenCollections.includes(collection.id)"
                class="icon collection-option">
                <i class="mdi mdi-eye-off" />
              </span>
              <span
                v-else
                class="icon collection-option">
                <i class="mdi mdi-eye" />
              </span>
            </div>
          </b-tooltip>
          <b-tooltip
            label="Rename"
            type="is-black"
            position="is-bottom"
            :delay="150"
            square>
            <div class="collection-item-rename" @click="toggleShowRename(collection.id)">
              <span class="icon collection-option">
                <i class="mdi mdi-pencil" />
              </span>
            </div>
          </b-tooltip>
          <b-tooltip
            label="Delete"
            type="is-black"
            position="is-bottom"
            :delay="150"
            square>
            <div
              class="collection-item-delete"
              @click="deleteCollection({ collectionId: collection.id, parentId: collection.parent.id })">
              <span class="icon collection-option">
                <i class="mdi mdi-delete" />
              </span>
            </div>
          </b-tooltip>
        </div>
        <div
          class="collection-item-left"
          :style="{ 'paddingLeft': `${indent(collection) * 1}rem`}">
          <span
            class="icon icon-nav-item"
            @click="toggleChildren(collection.id)">
            <i :class="getIconClass(collection.id)" />
          </span>
          <RenameCollection
            v-if="showRenameFor === collection.id"
            :currentName="collection.name"
            :id="collection.id"
            @cancel="toggleShowRename(null)"
            @rename="renameCollection" />
          <div
            v-else
            class="collection-item-text"
            @click="toggleChildren(collection.id)">
            {{ collection.name }}
            <span
              class="add-child-project"
              v-show="collection.id > 0"
              @click.stop="addNewCollection(collection.id)">+</span>
          </div>
        </div>
      </div>

      <!-- CHILDREN -->
      <ul
        v-if="displayChildrenOf.includes(collection.id)"
        :ref="`sub${collection.id}`"
        class="sub"
        :class="{ 'sub-active': displayChildrenOf.includes(collection.id) }">
        <CollectionManagementItem
          :collections="collection.children"
          :isRoot="false" />
      </ul>
    </li>

    <!-- ADD COLLECTION (root level) -->
    <li v-if="isRoot">
      <div class="collection-item collection-item-add">
        <div class="collection-item-actions" />
        <div
          class="collection-item-left">
          <span class="icon icon-nav-item" />
          <div
            class="collection-item-text"
            @click="addNewCollection(0)">
            <span>Add collection</span>
          </div>
        </div>
      </div>
    </li>

    <div
      v-if="isRoot && movingCollection"
      class="move-to-root"
      @dragover.prevent
      @dragenter="dragenterHandler($event, collectionNodes['0'])"
      @drop="dropHandler($event, collectionNodes['0'])">
      Move to main list
    </div>

  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import RenameCollection from '@/components/AppView/Sidebar/RenameCollection.vue';

export default {
  name: 'CollectionManagementItem',
  components: {
    RenameCollection,
  },
  props: {
    collections: Array,
    isRoot: Boolean,
  },
  data() {
    return {
      displayChildrenOf: [],
      showAddChildFor: null,
      showRenameFor: false,
      updatedCollectionName: null,
      showContextMenuOf: null,
      dragOver: null,
      dragExpandChildrenTimer: null,
      contextMenuPosition: {
        top: 0,
        left: 0,
      },
    };
  },
  computed: {
    ...mapState({
      collectionNodes: state => state.collections.collectionNodes,
      userId: state => state.users.internalUserId,
      highlightDragOverCollection: state => state.appState.highlightDragOverCollection,
      movingCollection: state => state.appState.movingCollection,
      hiddenCollections: state => state.users.settings.hiddenCollections,
    }),
    sortedCollections() {
      const collectionsToSort = this.collections.slice();
      const sorted = collectionsToSort.sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
      return sorted;
    },
  },
  methods: {
    ...mapActions({
      addCollection: 'collections/addCollection',
      vDeleteCollection: 'collections/deleteCollection',
      vUpdateCollection: 'collections/updateCollection',
      vMoveCollection: 'collections/moveCollection',
      updateHiddenCollections: 'users/updateHiddenCollections',
      deleteAllFilesInCollection: 'files/deleteAllFilesInCollection',
    }),
    ...mapMutations({
      setHighlightDragOverCollection: 'appState/SetHighlightDragOverCollection',
      setMovingCollection: 'appState/SetMovingCollection',
    }),
    indent(collection) {
      let indent = 0;
      let currentlyChecking = collection;
      while (currentlyChecking.parent.id !== 0) {
        indent += 1;
        currentlyChecking = currentlyChecking.parent;
      }
      return indent;
    },
    toggleHideCollection(collection) {
      const isHidden = this.hiddenCollections.find(hiddenCollectionId => hiddenCollectionId === collection.id);

      // Helper function for gathering descendants
      const descendants = ((coll) => {
        const list = [];
        return {
          gather: function(coll) { // eslint-disable-line 
            coll.children.forEach((child) => {
              list.push(child.id);
              if (child.children.length) descendants.gather(child);
            });
            return list;
          },
        };
      })();

      // Gather collectionId + descendant collection ids
      const collectionList = descendants.gather(collection);
      collectionList.push(collection.id);

      if (!isHidden) {
        this.hideCollections(collectionList);
        return;
      }

      // We're unhiding a collection, so need to add parent collections to the list
      let collectionToCheck = collection.parent;
      while (collectionToCheck.id !== 0) {
        collectionList.push(collectionToCheck.id);
        collectionToCheck = collectionToCheck.parent;
      }
      this.unhideCollections(collectionList);
    },
    hideCollections(ids) {
      const updatedHiddenCollections = this.hiddenCollections.slice();
      ids.forEach((collectionId) => {
        if (this.hiddenCollections.find(hiddenCollectionId => hiddenCollectionId === collectionId)) return;
        updatedHiddenCollections.push(collectionId);
      });
      this.updateHiddenCollections(updatedHiddenCollections);
    },
    unhideCollections(ids) {
      const updatedHiddenCollections = this.hiddenCollections.slice();
      ids.forEach((collectionId) => {
        const index = updatedHiddenCollections.findIndex(hiddenCollectionId => hiddenCollectionId === collectionId);
        if (index < 0) return;
        updatedHiddenCollections.splice(index, 1);
      });
      this.updateHiddenCollections(updatedHiddenCollections);
    },
    dragstartHandler(event, collection) {
      event.dataTransfer.setData('text/plain', `${JSON.stringify(collection)}`);
      event.dataTransfer.dropEffect = 'copy'; // eslint-disable-line no-param-reassign
    },
    dropHandler(event, collection) {
      this.setHighlightDragOverCollection(null);
      const { id: newParentId } = collection;
      this.vMoveCollection({ newParentId, collectionId: this.movingCollection });
      this.toggleMoveCollection();
    },
    dragenterHandler(event, collection) {
      if (collection.id === this.movingCollection) return;

      const { id: newParentId } = collection;
      this.setHighlightDragOverCollection(newParentId);
    },
    toggleMoveCollection(collectionId) {
      if (this.movingCollection === collectionId) this.setMovingCollection(null);
      else this.setMovingCollection(collectionId);
    },
    renameCollection(newName, collectionId) {
      this.vUpdateCollection({ collectionId, name: newName });
      this.toggleShowRename(null);
    },
    toggleShowRename(collectionId) {
      this.showRenameFor = collectionId;
      this.showContextMenuOf = null;
    },
    deleteCollection({ collectionId, parentId }) {
      this.$buefy.dialog.confirm({
        message: `All cards in <em>${this.collectionNodes[collectionId].name}</em> will be deleted, as well as child collections and their cards.<br><br>
                  <em style="font-size: 0.85rem;">Note that this cannot be undone.</em><br>`,
        cancelText: 'Cancel',
        confirmText: `Delete ${this.collectionNodes[collectionId].name}`,
        type: 'is-danger',
        hasIcon: false,
        onConfirm: () => {
          // First go over all cards in this collection and child collections, and delete all items

          // A helper function
          const descendants = ((collection) => {
            const list = [];
            return {
              gather: function(collection) { // eslint-disable-line 
                collection.children.forEach((child) => {
                  list.push(child.id);
                  if (child.children.length) descendants.gather(child);
                });
                return list;
              },
            };
          })();

          this.deleteAllFilesInCollection(collectionId);
          const descendantIds = descendants.gather(this.collectionNodes[collectionId]);
          descendantIds.forEach(id => this.deleteAllFilesInCollection(id));

          // Delete collection itself
          this.vDeleteCollection({ collectionId, parentId });
        },
      });
    },
    toggleChildren(collectionId, force) {
      if (!this.collectionNodes[collectionId].children.length && !force) return;

      const index = this.displayChildrenOf.findIndex(id => id === collectionId);
      if (index === -1) this.displayChildrenOf.push(collectionId);
      else this.displayChildrenOf.splice(index, 1);
    },
    getIconClass(collectionId) {
      const hasChildren = !!this.collectionNodes[collectionId].children.length;
      const showsChildren = this.displayChildrenOf.includes(collectionId);

      return {
        'mdi mdi-menu-up': hasChildren && showsChildren,
        'mdi mdi-menu-down': hasChildren && !showsChildren,
        '': !hasChildren,
      };
    },
    addNewCollection(parentId) {
      this.$buefy.dialog.prompt({
        message: 'Enter a name for the collection',
        trapFocus: true,
        onConfirm: (name) => {
          const newCollection = {
            name,
            notes: [],
          };
          const newMembership = {
            parentCollectionId: parentId,
            relatedUserId: this.userId,
            collectionAccessLevel: 1,
          };
          this.addCollection({ newCollection, newMembership });
          this.showAddChildFor = null;
          const index = this.displayChildrenOf.findIndex(id => id === parentId);
          if (index === -1) this.displayChildrenOf.push(parentId);
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/colors';
@import '~styles/fonts';

.container {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  max-width: none;
}
.root-container {
  height: 90vh;
  overflow-y: auto;
  padding: 0.25rem 0 15vh;
}
.collection-item-container {
  position: relative;
  background: inherit;
}
.collection-item-actions {
  flex: 0 0 10rem;
  justify-content: center;
}
.collection-item-actions div {
  padding: 0 0.25rem;
}
ul, li {
  list-style: none;
  font-weight: 300;
  position: relative;
  user-select: none;
}
li {
  margin: 0 !important;
}
.sub {
  height: 0px;
  position: relative;
  margin: 0;
  list-style: none;
  flex-grow: 1;
}
.sub-active {
  height: auto;
}
.collection-item {
  cursor: pointer;
  display: flex;
  padding: 0.125rem;
  padding-left: 1rem;
}
.collection-item div {
  display: inline-flex;
}
.collection-item:hover {
  background-color: $primary;
  transition: background-color 0.2s;
}
.collection-item-add {
  background-color: transparent !important;
  cursor: default;
}
.collection-item-add .collection-item-text span {
  color: $secondary-dark;
  font-family: $headerFont;
  font-size: 0.9rem;
  cursor: pointer;
}
.collection-item-add .collection-item-text span:hover {
  text-decoration: underline;
}
.collection-item-left {
  flex-basis: 20rem;
  flex-grow: 1;
  flex-shrink: 0;
  float: left;
}
.collection-item-right {
  flex-basis: 30vw;
  flex-basis: 7.5rem;
  flex-grow: 0;
  flex-shrink: 0;
  margin-right: 5vw;
  // justify-content: space-evenly;
  float: right;
}
.collection-item-right div {
  padding: 0rem 0.75rem;
}
.collection-item-text {
  flex-grow: 1;
  font-family: $headerFont;
  padding-left: 0.25rem;
  padding-top: 0.15rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.collection-item:hover .icon-nav-item {
  opacity: 1;
}
.icon-nav-item {
  opacity: 0.4;
  padding-top: 0.125rem;
}
.collection-item:hover .add-child-project {
  opacity: 1;
}
.add-child-project {
  margin-left: 0.75rem;
  padding: 0;
  text-align: center;
  font-weight: 700;
  font-size: 1.25rem;
  line-height: 1.25rem;
  border-radius: 50%;
  width: 1.25rem;
  height: 1.25rem;
  background-color: darken($primary-dark, 15);
  opacity: 0;
  color: rgba(255, 255, 255, 1);
  transition: all 0.2s;
}
.add-child-project-to-root {
  margin-left: 0.75rem;
  margin-top: 1rem;
  padding: 0;
  text-align: center;
  font-weight: 700;
  font-size: 1.25rem;
  line-height: 1.75rem;
  border-radius: 50%;
  width: 1.75rem;
  height: 1.75rem;
  background-color: darken($primary-dark, 15);
  opacity: 0.7;
  color: rgba(255, 255, 255, 1);
  cursor: pointer;
  transition: all 0.2s;
}
.add-child-project-to-root:hover {
  opacity: 1;
}
.no-children {
  cursor: default !important;
}
.icon {
  height: 100%;
  width: 1rem;
  cursor: pointer;
}
.collection-option {
  color: $black;
  transition: all 0.2s;
  opacity: 0.5;
}
.collection-item:hover .collection-option {
  opacity: 0.7;
}
.collection-option-move {
  opacity: 0.7;
}
.collection-item:hover .collection-option-move {
  opacity: 0.85;
}
.collection-option:hover {
  opacity: 1 !important;
}
.icon-add-child {
  padding-left: 0.5rem;
  color: $darkgreyText;
  opacity: 0.4;
  transition: all 0.4s;
}
.new-collection i {
  cursor: default !important;
}
.collection-item-input {
  background: transparent;
  border: 0;
  outline: none;
  font-family: $headerFont;
  font-style: italic;
  font-weight: 300;
  padding-left: 0.25rem;
  color: $darkgreyText;
}
.move-icon {
  cursor: grab;
}
.move-icon:active {
  cursor: grabbing;
}
.drag-over {
  font-weight: bold;
  transition: all 0.1s;
}
.move-to-root {
  font-style: italic;
  margin: 0.5rem 0 0 2rem;
  padding: 0.5rem 0 0.5rem 1.5rem;
  border-radius: 0.25rem;
  font-size: 0.9rem;
  background: $primary;
  width: 20rem;
}
</style>
